// ============================================================================
// SYSTELOS - PageSection Component
// ============================================================================
// Card/seção branca padronizada para conteúdo de página
// Versão: 1.3 | Atualizado: 26/01/2026
// CORRIGIDO: titulo agora aceita ReactNode (JSX com ícone, etc)
// ============================================================================

import { ReactNode } from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface PageSectionProps {
  children: ReactNode;
  /** Título da seção (opcional) - pode ser string ou ReactNode */
  titulo?: ReactNode;
  /** Subtítulo da seção (opcional) */
  subtitulo?: string;
  /** Ícone ao lado do título (ReactNode - pode ser <Icon /> ou componente) */
  icon?: ReactNode;
  /** Ações no header da seção (opcional) */
  headerActions?: ReactNode;
  /** Variante de estilo */
  variant?: 'default' | 'compact' | 'flat';
  /** Padding interno */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Margem inferior */
  marginBottom?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SHADOW_STYLES: Record<string, React.CSSProperties> = {
  default: { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
  compact: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  flat: { boxShadow: 'none', border: '1px solid #E5E7EB' },
};

const PADDING_CLASSES: Record<string, string> = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4 md:p-5',
  lg: 'p-5 md:p-6',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function PageSection({
  children,
  titulo,
  subtitulo,
  icon,
  headerActions,
  variant = 'default',
  padding = 'md',
  marginBottom = true,
  className = '',
}: PageSectionProps) {
  const paddingClass = PADDING_CLASSES[padding];
  const shadowStyle = SHADOW_STYLES[variant];
  const hasHeader = titulo || headerActions;

  return (
    <div
      className={`
        bg-white
        rounded-xl
        ${marginBottom ? 'mb-6' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={shadowStyle}
    >
      {/* Header da Seção */}
      {hasHeader && (
        <div className={`
          flex items-center justify-between gap-4
          ${padding !== 'none' ? 'px-4 md:px-5 py-3 md:py-4' : 'p-4'}
          ${children ? 'border-b border-gray-200' : ''}
        `}>
          <div className="flex items-center gap-3">
            {icon && (
              <div className="text-gray-500">
                {icon}
              </div>
            )}
            <div>
              {titulo && (
                <h2 className="text-lg font-semibold text-gray-900">
                  {titulo}
                </h2>
              )}
              {subtitulo && (
                <p className="text-sm text-gray-500">
                  {subtitulo}
                </p>
              )}
            </div>
          </div>

          {headerActions && (
            <div className="flex items-center gap-2">
              {headerActions}
            </div>
          )}
        </div>
      )}

      {/* Conteúdo */}
      {children && (
        <div className={hasHeader ? paddingClass : paddingClass}>
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Seção para filtros (estilo compacto)
 */
export function FilterSection({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <PageSection
      variant="compact"
      padding="md"
      className={className}
    >
      {children}
    </PageSection>
  );
}

/**
 * Seção de conteúdo vazio (empty state)
 */
export function EmptySection({
  icon,
  titulo,
  descricao,
  acao,
  className = '',
}: {
  icon?: ReactNode;
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
  className?: string;
}) {
  return (
    <PageSection className={className}>
      <div className="text-center py-12">
        {icon && (
          <div className="flex justify-center mb-4 text-gray-300">
            {icon}
          </div>
        )}
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {titulo}
        </h3>
        {descricao && (
          <p className="text-gray-500 mb-4">
            {descricao}
          </p>
        )}
        {acao}
      </div>
    </PageSection>
  );
}

// ============================================================================
// COMPOUND EXPORTS
// ============================================================================

PageSection.Filter = FilterSection;
PageSection.Empty = EmptySection;

export default PageSection;

